// src/lib/pharmacyFirst/pgds/aht/pgd-aht-wt-foundayo.ts
//
// Orforglipron (Foundayo®) film-coated tablets PGD — AHT weight management
// Source: AHT Foundayo PGD v2.1 (28/10/2026 to 28/10/2027), public/pgd-docs/aht/weight/
// The developer change list cites v2.0 (06/10/2026 to 05/10/2027); v2.1 is the version in the repo (OI-F1).
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
// Quantity basis: 1 × 30-tablet pack of a single strength = 30-day supply. Never split a 90-tablet pack.

import type { PGD } from "../../types";

const FDY_TABLET_DIRECTIONS =
  "Take ONE tablet once daily at any time of day, with or without food. Swallow whole; do not break, crush or chew. Never take more than one tablet in a day.";

export const pgdAhtWtFoundayo: PGD = {
  id: "pgd-aht-wt-foundayo",
  reference: "AHT-WT-FDY",
  // SIGN-OFF REQUIRED: OI-F1
  version: "2.1",
  drugRef: "foundayo",
  indication: "Weight management — orforglipron (Foundayo®) film-coated tablets",
  // SIGN-OFF REQUIRED: OI-F1
  validFrom: "2026-10-28",
  expiryDate: "2027-10-28",
  frameworkType: "other",
  service: "aht_private",
  commissioner: "Allied Health Training Limited",
  status: "active",

  population: {
    ageRange: { min: 18, max: 84 },
    sex: "any",
    pregnancyAllowed: false,
    breastfeedingAllowed: false,
  },

  consultationRequirements: {
    // PGD: consultation at a registered clinic; remote delivery permitted under the conditions in Inclusion criteria.
    initialSupply: "remote_permitted",
    subsequentSupply: "remote_permitted",
  },

  duration: {
    days: 30,
    comment: "30-day supply per dose step (1 × 30-tablet pack of a single strength). Monthly review is mandatory.",
  },

  doseBands: [
    {
      id: "fdy-step1",
      doseStepId: "fdy-0_8mg",
      resolveBy: "single",
      dose: 0.8,
      frequency: "Once daily",
      comment: "Month 1: initiation dose. Start here for new patients and for switchers from another incretin.",
      formulations: [
        {
          formulationId: "foundayo-0_8mg-tablet",
          instruction: `${FDY_TABLET_DIRECTIONS} Confirm imprint G1.`,
          quantity: { count: 1, label: "1 × Foundayo 0.8 mg tablets, 30-tablet pack" },
          allowQuantityOverride: false,
        },
      ],
    },
    {
      id: "fdy-step2",
      doseStepId: "fdy-2_5mg",
      resolveBy: "single",
      dose: 2.5,
      frequency: "Once daily",
      comment: "Month 2. At least 30 days must be completed at the current dose before any increase.",
      formulations: [
        {
          formulationId: "foundayo-2_5mg-tablet",
          instruction: `${FDY_TABLET_DIRECTIONS} Confirm imprint G2.`,
          quantity: { count: 1, label: "1 × Foundayo 2.5 mg tablets, 30-tablet pack" },
          allowQuantityOverride: false,
        },
      ],
    },
    {
      id: "fdy-step3",
      doseStepId: "fdy-5_5mg",
      resolveBy: "single",
      dose: 5.5,
      frequency: "Once daily",
      comment: "One step at a time, based on response and tolerability, at least 30 days after the previous step.",
      formulations: [
        {
          formulationId: "foundayo-5_5mg-tablet",
          instruction: `${FDY_TABLET_DIRECTIONS} Confirm imprint G3.`,
          quantity: { count: 1, label: "1 × Foundayo 5.5 mg tablets, 30-tablet pack" },
          allowQuantityOverride: false,
        },
      ],
    },
    {
      id: "fdy-step4",
      doseStepId: "fdy-9mg",
      resolveBy: "single",
      dose: 9,
      frequency: "Once daily",
      comment:
        "One step at a time. Also the maximum dose where a strong CYP3A4 inhibitor or a clinical OATP1B inhibitor (e.g. ciclosporin) is taken.",
      formulations: [
        {
          formulationId: "foundayo-9mg-tablet",
          instruction: `${FDY_TABLET_DIRECTIONS} Confirm imprint G4.`,
          quantity: { count: 1, label: "1 × Foundayo 9 mg tablets, 30-tablet pack" },
          allowQuantityOverride: false,
        },
      ],
    },
    {
      id: "fdy-step5",
      doseStepId: "fdy-14_5mg",
      resolveBy: "single",
      dose: 14.5,
      frequency: "Once daily",
      comment: "One step at a time, at least 30 days after the previous step.",
      formulations: [
        {
          formulationId: "foundayo-14_5mg-tablet",
          instruction: `${FDY_TABLET_DIRECTIONS} Confirm imprint G5.`,
          quantity: { count: 1, label: "1 × Foundayo 14.5 mg tablets, 30-tablet pack" },
          allowQuantityOverride: false,
        },
      ],
    },
    {
      id: "fdy-step6",
      doseStepId: "fdy-17_2mg",
      resolveBy: "single",
      dose: 17.2,
      frequency: "Once daily",
      comment: "MAXIMUM dose: 17.2 mg once daily.",
      formulations: [
        {
          formulationId: "foundayo-17_2mg-tablet",
          instruction: `${FDY_TABLET_DIRECTIONS} Confirm imprint G6.`,
          quantity: { count: 1, label: "1 × Foundayo 17.2 mg tablets, 30-tablet pack" },
          allowQuantityOverride: false,
        },
      ],
    },
  ],

  // Every exclusion in the PGD v2.1 "Exclusion criteria" list appears below, in the PGD's order.
  // Where a check's wording differs from the drug-selection question it must not be auto-answered from it.
  preSupplyChecks: [
    // INCLUSION
    {
      id: "inc-age-18-84",
      question: "Is the patient aged 18–84 years?",
      direction: "exclude_on_no",
      autoAnswerFrom: "age_18_to_84",
    },
    {
      id: "inc-bmi-meets-threshold",
      question:
        "Does the patient meet the BMI inclusion threshold? (BMI ≥30; or BMI 27 to <30 with ≥1 weight-related comorbidity; or BMI ≥27.5 in South Asian, Chinese, other Asian, Middle Eastern, Black African, or African-Caribbean ethnic backgrounds)",
      direction: "exclude_on_no",
      autoAnswerFrom: "sharedBmiInclusionMet",
    },
    {
      id: "inc-monitoring-consent",
      question: "Has the patient consented to monthly reviews and monitoring?",
      direction: "exclude_on_no",
      autoAnswerFrom: "sharedMonitoringConsent",
    },
    {
      id: "inc-lifestyle-agreement",
      question: "Has the patient agreed to a reduced-calorie diet and increased physical activity?",
      direction: "exclude_on_no",
      autoAnswerFrom: "sharedLifestyleAgreement",
    },
    {
      id: "inc-informed-consent",
      question: "Has the patient provided informed consent?",
      direction: "exclude_on_no",
      autoAnswerFrom: "sharedInformedConsent",
    },
    {
      id: "inc-identity-verified",
      question:
        "Has the patient's identity been verified by photographic ID checked in person or during a live video consultation, and the method recorded?",
      direction: "exclude_on_no",
    },
    {
      id: "inc-height-weight-verified",
      question:
        "Have height and weight been independently verified at this consultation (measured in person, observed live on video, or supplied directly by the GP or another registered professional), and the method recorded? Self-declared values, photographs and pre-recorded video are not acceptable.",
      direction: "exclude_on_no",
    },
    {
      id: "inc-consultation-conditions",
      question:
        "Is the patient attending at the registered premises, or is this a compliant remote consultation (pharmacist present at the registered premises, real-time two-way audio and video, not questionnaire alone)?",
      direction: "exclude_on_no",
      autoAnswerFrom: "sharedFaceToFace",
    },

    // EXCLUSION CRITERIA — Weight and monitoring
    {
      id: "ex-bmi-below-27",
      question:
        "Is the patient's BMI below 27 kg/m² at initiation (and not meeting the re-initiation criteria)?",
      autoAnswerFrom: "bmiBelowThreshold",
    },
    {
      id: "ex-bmi-27-30-no-comorbidity-not-ethnic",
      question:
        "Is the patient's BMI 27 to below 30 kg/m² with no weight-related comorbidity, and NOT from a South Asian, Chinese, other Asian, Middle Eastern, Black African or African-Caribbean background?",
    },
    {
      id: "ex-bmi-below-27-5-no-comorbidity-ethnic",
      question:
        "Is the patient's BMI below 27.5 kg/m² with no weight-related comorbidity, and from a South Asian, Chinese, other Asian, Middle Eastern, Black African or African-Caribbean background?",
    },
    {
      id: "ex-bmi-below-22-5-during-treatment",
      question: "Has the patient's BMI fallen below 22.5 kg/m² while on orforglipron treatment?",
      autoAnswerFrom: "bmiBelowFloor225",
    },
    {
      id: "ex-bmi-monitoring-refusal",
      question: "Is the patient refusing to participate in BMI monitoring?",
      autoAnswerFrom: "sharedMonitoringRefusal",
    },
    {
      id: "ex-efficacy-failure",
      question:
        "Has the patient been unable to achieve ≥5% weight loss after 6 months at the maximum tolerated dose?",
    },

    // Product and hypersensitivity
    {
      id: "ex-hypersensitivity-orforglipron",
      question:
        "Does the patient have known hypersensitivity to orforglipron or to any of the excipients (refer to the PIL)?",
    },
    {
      id: "ex-anaphylaxis-incretin",
      question:
        "Has the patient had a previous anaphylactic reaction to any GLP-1 receptor agonist or other incretin mimetic?",
    },
    {
      id: "ex-unable-to-swallow-whole",
      question: "Is the patient unable to swallow the tablet whole? (Tablets must not be broken, crushed or chewed.)",
    },

    // Clinical status
    {
      id: "ex-pregnancy-breastfeeding-planning",
      question:
        "Is the patient pregnant, breastfeeding, or planning pregnancy? (Orforglipron must be discontinued at least 3 weeks before a planned pregnancy.)",
    },
    {
      id: "ex-contraception-unwilling",
      question:
        "Is the patient a woman of childbearing potential who is unwilling or unable to use effective contraception?",
    },
    {
      id: "ex-severe-hepatic",
      question: "Does the patient have severe hepatic impairment (Child-Pugh C)?",
    },
    {
      id: "ex-nyha-iv",
      question: "Does the patient have congestive heart failure NYHA class IV?",
      autoAnswerFrom: "q19NyhaIV",
    },
    {
      id: "ex-severe-gi",
      question: "Does the patient have severe or unstable gastrointestinal disease, including severe gastroparesis?",
    },
    {
      id: "ex-pancreatitis",
      question: "Does the patient have a history of pancreatitis?",
    },
    {
      id: "ex-t1dm-t3c",
      question: "Does the patient have type 1 or type 3c diabetes mellitus?",
    },
    {
      id: "ex-t2dm-hba1c",
      question:
        "Does the patient have type 2 diabetes where the most recent HbA1c is above 86 mmol/mol (10%), or where no HbA1c result from within the preceding 6 months is available?",
    },
    {
      id: "ex-retinopathy-or-macular-oedema",
      question:
        "Does the patient have diabetic retinopathy or macular oedema, or are they currently receiving or planning to receive treatment for either?",
      autoAnswerFrom: "q18RetinopathyAny",
    },
    {
      // Not auto-answered: the shared assessment asks only about a condition under specialist care.
      id: "ex-endocrine-obesity",
      question:
        "Is the patient's obesity known or suspected to be secondary to an untreated or undiagnosed endocrine condition, or is the patient under specialist care for such a condition?",
    },
    {
      id: "ex-eating-disorder",
      question:
        "Does the patient have a current or previous eating disorder (anorexia nervosa, bulimia nervosa, binge eating disorder, or other specified feeding or eating disorder), without written confirmation from a specialist eating disorder or mental health service that treatment is appropriate?",
      hint: "If excluded for a suspected or known eating disorder: refer to the GP with the patient's consent and signpost to eating disorder support. Do not simply decline and close the consultation.",
    },
    {
      id: "ex-disordered-eating-features",
      question:
        "Does the patient currently show features suggestive of disordered eating: self-induced vomiting, laxative or diuretic misuse for weight control, compulsive exercise, or a preoccupation with weight or shape that you assess as disproportionate?",
      hint: "If excluded: refer to the GP with the patient's consent and signpost to eating disorder support.",
    },

    // Medicines
    {
      id: "ex-insulin-sulfonylurea",
      question:
        "Is the patient currently using insulin or a sulfonylurea (for example gliclazide, glibenclamide)?",
      hint: "Reducing these doses is a prescribing decision outside the scope of this PGD.",
      autoAnswerFrom: "q6SulphonylureaInsulin",
    },
    {
      id: "ex-concurrent-weight-loss-agent",
      question:
        "Is the patient using any other weight loss agent (for example orlistat, Mysimba), GLP-1 receptor agonist or other incretin mimetic? (Transfer from orforglipron hard capsules to tablets is a change of formulation, not concurrent use.)",
      autoAnswerFrom: "sharedConcurrentWeightAgent",
    },
    {
      id: "ex-ritonavir-telaprevir",
      question: "Is the patient taking ritonavir or telaprevir?",
    },
    {
      id: "ex-strong-cyp3a4-inducer",
      question:
        "Is the patient taking a strong CYP3A4 inducer (for example rifampicin, phenytoin, carbamazepine, St John's wort)?",
    },
    {
      id: "ex-oral-topotecan",
      question:
        "Is the patient taking oral topotecan WITHOUT written confirmation from their oncology team that supply is appropriate (recorded)?",
    },
    {
      id: "ex-simvastatin-rosuvastatin",
      question:
        "Is the patient taking simvastatin, or rosuvastatin above 20 mg daily, WITHOUT the prescriber of that medicine having been contacted and the required dose change or monitoring documented before supply?",
    },
    {
      // v2.1 addition (GP notification and information sharing).
      id: "ex-gp-sharing-risk-assessment",
      question:
        "Has the patient declined consent to share information with their GP, or have no registered GP, where the recorded risk-based assessment does not support supply?",
      hint: "The risk-based assessment must record the reason given, the specific risks considered and why supply is still safe, and confirm safety netting was given and the patient was advised to tell their GP themselves.",
    },
  ],

  interactions: [
    {
      id: "int-cyp3a4-oatp1b-dose-cap",
      interactionName: "Strong CYP3A4 inhibitor or clinical OATP1B inhibitor (maximum 9 mg)",
      question:
        "Is the patient taking a strong CYP3A4 inhibitor (ketoconazole, clarithromycin, itraconazole) or a clinical OATP1B inhibitor (ciclosporin)?",
      affectedDrugs: ["foundayo"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
    {
      id: "int-moderate-cyp3a4-inducer",
      interactionName: "Moderate CYP3A4 inducer (bosentan, efavirenz)",
      question: "Is the patient taking a moderate CYP3A4 inducer (bosentan, efavirenz)?",
      affectedDrugs: ["foundayo"],
      excludeIfUnmanageable: false,
      managementPlanRequired: true,
    },
  ],

  cautions: [
    { id: "caut-gi-effects", text: "Gastrointestinal reactions are very common, highest during dose escalation. Consider a dose reduction or discontinuation where symptoms are severe." },
    { id: "caut-dehydration-aki", text: "Dehydration and acute kidney injury: advise fluid intake; take particular care in older patients, those with renal impairment and those on diuretics, ACE inhibitors or ARBs. Same day assessment if fluids cannot be kept down." },
    { id: "caut-pancreatitis", text: "Suspected acute pancreatitis: stop immediately, do not supply further, same day emergency assessment, notify the GP with consent. If confirmed, never restart." },
    { id: "caut-hypotension", text: "Hypotension: check BP at each review in patients on antihypertensives; refer if symptomatic." },
    { id: "caut-tachycardia", text: "Tachycardia: check resting pulse at each review; refer to the GP if persistently above 100 bpm or palpitations, syncope or presyncope." },
    { id: "caut-gastric-emptying", text: "Impaired gastric emptying and gastroparesis: persistent bloating or abdominal pain requires review and consideration of discontinuation." },
    { id: "caut-aspiration", text: "Pulmonary aspiration: advise patients to tell any anaesthetist, surgeon or endoscopist before a planned procedure." },
    { id: "caut-gallbladder", text: "Acute gallbladder disease: refer to the GP for imaging and follow-up, or for same day assessment with fever, jaundice or severe right upper quadrant pain." },
    { id: "caut-diabetic-retinopathy", text: "Diabetic retinopathy: rapid glucose improvement can transiently worsen it; patients with a history must be monitored by their diabetes team." },
    { id: "caut-naion", text: "NAION: advise patients to report any sudden change or loss of vision; urgent same day eye assessment; stop if NAION is confirmed." },
    { id: "caut-hypoglycaemia", text: "Hypoglycaemia risk is increased with insulin or an insulin secretagogue; those patients are excluded." },
    { id: "caut-oral-contraception", text: "Oral hormonal contraception may be less effective: use a non-oral method, or add a barrier method, for 30 days after initiation and for 30 days after every dose increase." },
    { id: "caut-hrt-progestogen", text: "Oral progestogen in HRT: do not adjust HRT under this PGD; refer the patient to the HRT prescriber before or soon after starting and at each dose increase, and document this." },
    { id: "caut-mood", text: "Mood and behaviour: ask about new or worsening low mood or anxiety at each monthly review. Any thoughts of self harm: same day medical assessment and do not supply further." },
    { id: "caut-disordered-eating", text: "At initiation and every monthly review ask directly about binge eating, self-induced vomiting, laxative or diuretic use for weight control, and compulsive exercise. If disordered eating is suspected, stop, do not supply further, and refer to the GP." },
    { id: "caut-allergy", text: "Serious allergic reaction including anaphylaxis and angioedema: discontinue if suspected." },
    { id: "caut-clinical-judgement", text: "The practitioner retains the right to refuse supply on clinical judgement even where all criteria are met. Record the decision and the reason." },
  ],

  counselling: [
    "One tablet once daily at any time of day, with or without food; swallow whole, do not break, crush or chew",
    "Never take more than one tablet in a day and never use more than one tablet to make up a higher dose",
    "Missed dose: resume as soon as possible; never take more than one tablet in a day and never double up",
    "The dose is increased no more often than every 30 days; the licensed maximum is 17.2 mg once daily; titration takes at least 5 months",
    "Provide the PIL and explain the black triangle status; report suspected side effects via the Yellow Card scheme",
    "Store in the original package, protected from light; no cold chain",
    "Tell any anaesthetist, surgeon, dentist or endoscopist that you take orforglipron before any procedure",
    "Women who could become pregnant: use a non-oral contraceptive method, or add a barrier method, for 30 days at the start and after every dose increase",
    "HRT with a progesterone tablet: speak to your HRT prescriber before or soon after starting and at each dose increase; do not change HRT yourself",
    "Pancreatitis warning: severe persistent abdominal pain radiating to the back with nausea and vomiting — seek immediate medical attention",
    "Report any sudden change or loss of vision immediately",
    "Maintain fluid intake, especially with vomiting or diarrhoea",
    "Hair loss has been reported, is usually transient and is associated with weight reduction",
    "Explain GP notification: consent to share is sought and notification recommended; if declined, the reason is recorded and the practitioner assesses whether supply is safe",
  ],

  efficacyReview: {
    timepoint: "6 months at the recorded maximum tolerated dose",
    threshold: "≥5% weight loss from initial body weight",
    actionIfNotMet: "Discontinue — stop treatment, refer to GP, offer lifestyle support",
  },

  monitoring: {
    frequency: "monthly",
    parameters: [
      "Verified weight and BMI",
      "Percentage change from initial body weight (recorded at first supply)",
      "Adherence, tolerability and side effects",
      "Resting pulse",
      "Blood pressure",
      "Waist circumference",
      "Mood and eating disorder screen",
      "Medicines reconciliation",
      "HbA1c at least every 6 months if type 2 diabetes",
    ],
    discontinueIf: [
      "Less than 5% weight loss after 6 months at the maximum tolerated dose",
      "BMI below 22.5 kg/m²",
      "Pancreatitis confirmed (never restart)",
      "NAION confirmed",
      "Pregnancy",
    ],
  },

  reviewSchedule: {
    supplyIntervalDays: 30,
    // SIGN-OFF REQUIRED: OI-F3 — the PGD starts the six month clock at the recorded maximum tolerated dose, not first supply.
    drugSpecificReviewDays: 180,
    drugSpecificReviewLabel: "6-month efficacy review (from maximum tolerated dose)",
    efficacyThresholdPercent: 5,
    stopRuleDescription:
      "Discontinue if less than 5% weight loss has been achieved after 6 months at the maximum tolerated dose",
    // SIGN-OFF REQUIRED: OI-F4 — the PGD defines no Section B review; 168 days copied from the other AHT weight drugs.
    sectionBReviewDays: 168,
    bmiFloorKgM2: 22.5,
    titrationSchedule: undefined,
    maxEpisodesBeforeReview: null,
    requiresBpPulseAtEscalation: false,
    requiresBrandAndBatchAtEverySupply: false,
  },

  adverseEventGuidance: [
    "Anaphylaxis/angioedema: STOP immediately — refer urgently; discontinue",
    "Suspected pancreatitis: STOP immediately, do not supply further, same day emergency assessment; do not restart if confirmed",
    "NAION: urgent same day eye assessment; discontinue if confirmed",
    "Thoughts of self harm: same day medical assessment and do not supply further",
    "Cannot keep fluids down: same day assessment",
    "Report locally, notify the GP with consent, and report via the MHRA Yellow Card scheme (yellowcard.mhra.gov.uk)",
    "Black Triangle medicine — all suspected ADRs must be reported",
  ],
};
